import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Camera, CheckCircle2, X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const YN_UC = ["Yes", "No", "Unable to confirm"];
const OPTS = {
  interconnected: ["Yes", "No", "Some only", "Unable to confirm"],
  self_levelling: ["Yes", "No", "Unable to confirm", "Not applicable"],
  supplies_house: ["Yes", "Partially", "Unable to confirm"],
  sample_points: ["Top of tank", "Mid-level", "Bottom of tank", "Tank outlet", "Kitchen tap", "Other"],
  bottom_sample: ["Yes", "No", "Unable to access"],
  sediment: ["None visible", "Light", "Moderate", "Heavy", "Unable to assess"],
  clarity: ["Clear", "Slightly cloudy", "Cloudy", "Very cloudy", "Unable to assess"],
  colour: ["Colourless", "Slight colour", "Yellow", "Brown", "Greenish", "Other", "Unable to assess"],
  odour: ["No obvious odour", "Earthy", "Musty", "Stagnant / organic", "Sulphur / rotten egg", "Chemical", "Other", "Unable to assess"],
  visible: ["No obvious issue", "Fine sediment", "Heavy sediment", "Floating debris", "Organic matter", "Biofilm / slime visible", "Insects / larvae visible", "Surface film", "Other"],
  lid: ["Secure", "Attention Suggested", "Unable to confirm"],
  screen: ["Present and intact", "Damaged", "Missing", "Unable to confirm"],
  screen_inlet: ["Present and intact", "Damaged", "Missing", "Unable to confirm", "Not Applicable"],
  entry: ["None observed", "Damaged lid", "Open access point", "Damaged/missing screen", "Inlet issue", "Overflow issue", "Animal/insect access possible", "Other"],
  catch_condition: ["Good", "Fair", "Needs Attention", "Unable to Confirm"],
  gutter: ["Clear", "Light debris", "Moderate debris", "Heavy debris", "Standing water", "Unable to inspect"],
  guard: ["Yes", "No", "Partial", "Unable to confirm"],
  leaf_fit: ["Yes", "No", "Some downpipes only", "Unable to confirm"],
  cond4: ["Good", "Attention Suggested", "Unable to confirm", "Not Applicable"],
  lead: ["Yes", "No", "Suspected", "Unable to confirm"],
  catch_obs: ["Roof/catchment appears generally clean", "Light debris", "Moderate debris", "Heavy debris", "Overhanging vegetation", "Bird/animal activity evident", "Moss / organic growth", "Rust / corrosion visible", "Roof coating / painted surface", "Recent roof work / repairs", "Downpipe issue", "Other visible concern"],
  catch_overall: ["Good", "Fair", "Attention Suggested", "Unable to Confirm"],
  filt_config: ["Single", "Twin", "Triple", "Other"],
  filt_media: ["Sediment", "Carbon", "Specialist media", "Unknown"],
  filt_cond: ["Appears within service period", "Appears overdue", "Service history unknown", "Filters reportedly blocking quickly", "Significant pressure restriction reported", "Unable to confirm"],
  uv_service: ["Within 12 months", "More than 12 months", "Unknown"],
  uv_obs: ["No obvious issue observed", "Attention Suggested", "Unable to confirm"],
  us_type: ["Sediment", "Carbon", "RO", "Other", "Unknown"],
  history: ["Tested within last 12 months", "Tested more than 12 months ago", "Never tested", "Owner unsure"],
  sample_src: ["Kitchen Tap", "Tank"],
  nc_reason: ["No suitable sample point", "Customer changed mind", "Kit unavailable", "Access issue", "Other"],
};
const TANK_MATS = [["poly", "Poly"], ["corrugated_steel", "Corrugated / steel"], ["concrete", "Concrete"], ["other", "Other household"]];
const STEPS = ["Tank Setup", "Water", "Catchment", "Treatment", "Sample"];
const SECTION_LABELS = { tank_setup: "Tank Setup", tank_water: "Tank Water", tank_condition: "Tank Condition", catchment: "Catchment", treatment: "Filtration & Treatment", sample: "Sample", additional: "Additional" };
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
const btn = "px-3 py-2 rounded-2xl font-semibold text-[13px] border-2 transition-colors text-left";

function Pick({ options, value, onPick, tid }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {options.map((o) => (
        <button key={o} data-testid={`${tid}-${slug(o)}`} type="button" onClick={() => onPick(o)}
          className={`${btn} ${value === o ? "bg-cyan-600 border-cyan-600 text-white" : "border-slate-200 bg-white text-slate-600"}`}>{o}</button>
      ))}
    </div>
  );
}
function MultiPick({ options, values, onToggle, tid }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {options.map((o) => (
        <button key={o} data-testid={`${tid}-${slug(o)}`} type="button" onClick={() => onToggle(o)}
          className={`${btn} ${(values || []).includes(o) ? "bg-cyan-600 border-cyan-600 text-white" : "border-slate-200 bg-white text-slate-600"}`}>{o}</button>
      ))}
    </div>
  );
}
function Q({ label, hint, children }) {
  return (
    <div className="mt-4">
      <div className="text-[13px] font-bold text-slate-800">{label}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      {children}
    </div>
  );
}

function Thumb({ inspId, photo, onRemove }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/field/inspection/${inspId}/photos/${photo.id}`, { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [inspId, photo.id]);
  return (
    <div className="relative w-20">
      {src ? <img src={src} alt="" className="w-20 h-16 object-cover rounded-lg border border-slate-200" />
        : <div className="w-20 h-16 rounded-lg bg-slate-100" />}
      {onRemove && (
        <button type="button" data-testid={`remove-photo-${photo.id}`} onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
      )}
      {photo.caption && <div className="text-[9px] text-slate-500 truncate">{photo.caption}</div>}
    </div>
  );
}

// Jun 2026 — compress on-device BEFORE upload. Phone cameras produce
// 3–12MB files; on rural mobile data that made each photo painfully
// slow. Downscale to ≤1600px JPEG (~200–500KB) locally — the server
// re-compresses anyway, so nothing is lost. Falls back to the original
// file if the browser can't decode it (e.g. exotic HEIC variants).
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (blob && blob.size > 0 && blob.size < file.size) {
      return new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
    }
  } catch {
    // fall through — server-side validation/compression still applies
  }
  return file;
}

// Contextual camera button — inherits section/question/finding metadata (spec §3-5).
function PhotoBtn({ insp, photos, category, section, questionKey, finding, tankKey, level = "optional", label, onChange, disabled }) {
  const [busyUp, setBusyUp] = useState(false);
  const [progress, setProgress] = useState(0);
  const [caption, setCaption] = useState("");
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const mine = photos.filter((p) => p.category === category
    && (!questionKey || p.question_key === questionKey)
    && (!tankKey || p.tank_key === tankKey));
  const upload = async (e) => {
    const f0 = e.target.files?.[0];
    if (!f0) return;
    setBusyUp(true);
    setProgress(0);
    const f = await compressImage(f0);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("category", category);
    fd.append("section", section);
    fd.append("question_key", questionKey || "");
    fd.append("finding", finding || "");
    fd.append("tank_key", tankKey || "");
    fd.append("caption", caption);
    try {
      await api.post(`/field/inspection/${insp.id}/photos`, fd, {
        onUploadProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      setCaption("");
      onChange();
    }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusyUp(false); setProgress(0); e.target.value = ""; }
  };
  const remove = async (pid) => {
    try { await api.delete(`/field/inspection/${insp.id}/photos/${pid}`); onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
  };
  if (level === "hidden") return null;
  return (
    <div data-testid={`photo-block-${category}${questionKey ? `-${questionKey}` : ""}${tankKey ? `-${tankKey}` : ""}`}
      className={`mt-2 rounded-xl p-2.5 ${level === "suggested" ? "bg-amber-50 border border-amber-300" : "bg-slate-50 border border-slate-200"}`}>
      {level === "suggested" && <div data-testid={`photo-suggested-${category}`} className="text-[11px] font-bold text-amber-700 mb-1.5">📷 Photo suggested for this finding</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={disabled || busyUp} onClick={() => camRef.current?.click()}
          data-testid={`take-photo-${category}${questionKey ? `-${questionKey}` : ""}${tankKey ? `-${tankKey}` : ""}`}
          className={`px-3 py-2 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${level === "suggested" ? "bg-amber-600 text-white" : "bg-slate-700 text-white"} disabled:opacity-50`}>
          <Camera className="w-3.5 h-3.5" /> {busyUp ? (progress > 0 && progress < 100 ? `Uploading… ${progress}%` : "Uploading…") : (label || "TAKE PHOTO")}
        </button>
        <button type="button" disabled={disabled || busyUp} onClick={() => fileRef.current?.click()}
          className="px-3 py-2 rounded-full text-xs font-bold border border-slate-300 text-slate-600">Upload existing</button>
        {mine.map((p) => <Thumb key={p.id} inspId={insp.id} photo={p} onRemove={disabled ? null : () => remove(p.id)} />)}
      </div>
      <input placeholder="Optional caption" maxLength={200} value={caption} onChange={(e) => setCaption(e.target.value)}
        className="mt-2 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid={`photo-caption-${category}`} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
    </div>
  );
}

// Mobile-first structured Water Health Inspection — household water system only.
export default function FieldInspection() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadStatus, setLoadStatus] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [s, setS] = useState({});
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [bookBusy, setBookBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/field/inspection/by-appointment/${appointmentId}`)
      .then(({ data: d }) => { setLoadError(null); setLoadStatus(null); setData(d); setS(d.inspection.sections || {}); if (d.inspection.status === "completed") setDone({ duplicate: true }); })
      .catch((e) => {
        setLoadStatus(e.response?.status || null);
        setLoadError(e.response?.data?.detail || "Failed to load inspection — check your connection and try again.");
      });
  }, [appointmentId]);
  useEffect(load, [load]);
  const reloadPhotos = useCallback(() => {
    api.get(`/field/inspection/by-appointment/${appointmentId}`)
      .then(({ data: d }) => setData((cur) => ({ ...cur, photos: d.photos }))).catch(() => {});
  }, [appointmentId]);

  const set = (key, patch) => setS((cur) => ({ ...cur, [key]: { ...(cur[key] || {}), ...patch } }));
  // Aug 2026 — per-tank records: counts drive an individual card per tank.
  // Reconciliation preserves any data already typed into surviving tanks.
  const setTankCount = (mat, n) => setS((cur) => {
    const cfg = cur.tank_config || {};
    const counts = { ...(cfg.counts || {}), [mat]: n };
    const prev = cfg.tanks || [];
    const tanks = [];
    TANK_MATS.forEach(([k, label]) => {
      const existing = prev.filter((t) => t.material === k);
      for (let i = 0; i < (counts[k] || 0); i++) {
        const base = existing[i] || {};
        tanks.push({ ...base, key: `${k}-${i + 1}`, material: k, label: `${label} Tank ${i + 1}` });
      }
    });
    return { ...cur, tank_config: { ...cfg, counts, tanks } };
  });
  const setTank = (key, patch) => setS((cur) => {
    const cfg = cur.tank_config || {};
    return { ...cur, tank_config: { ...cfg, tanks: (cfg.tanks || []).map((t) => (t.key === key ? { ...t, ...patch } : t)) } };
  });
  const toggle = (key, field, o) => setS((cur) => {
    const d = cur[key] || {};
    const list = d[field] || [];
    return { ...cur, [key]: { ...d, [field]: list.includes(o) ? list.filter((x) => x !== o) : [...list, o] } };
  });
  const save = async (silent = true) => {
    if (!data || done) return; // never autosave after completion (422 noise)
    try {
      await api.put(`/field/inspection/${data.inspection.id}`, { sections: s });
      if (!silent) toast.success("Saved");
    } catch (e) { if (!silent) toast.error(e.response?.data?.detail || "Save failed"); }
  };
  useEffect(() => {
    if (!data || done) return;
    const t = setTimeout(() => save(true), 3000);
    return () => clearTimeout(t);
  }, [s]); // eslint-disable-line

  // Waiting-for-payment: poll Stripe while a field payment is pending.
  // The poll endpoint is also what transitions the booking to paid
  // (webhook remains the source of truth) — the tech never marks paid.
  const pendingSession = data?.booking?.pending?.session_id;
  useEffect(() => {
    if (!pendingSession) return;
    const t = setInterval(async () => {
      try {
        const { data: st } = await api.get(`/payments/status/${pendingSession}`);
        if (st.payment_status === "paid") load();
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [pendingSession, load]);

  // Mirrors the server's required list — jumps the tech straight to
  // whatever they missed instead of a bare error toast.
  const missingRequired = () => {
    const req = [
      [0, "tank-supplies", "Do these tanks supply the house?", (s.tank_config || {}).supplies_house],
      [1, "sediment", "Sediment level", (s.tank_water || {}).sediment_level],
      [1, "clarity", "Water clarity", (s.tank_water || {}).clarity],
      [2, "catch-condition", "Overall condition of catchment area", (s.catchment || {}).condition],
      [2, "flashing", "Lead flashing", (s.catchment || {}).lead_flashing],
      [3, "filt-installed", "Whole-of-house filtration installed?", (s.filtration || {}).installed],
      [3, "uv-installed", "UV installed?", (s.uv || {}).installed],
      [4, "hist", "Water testing history", (s.testing_history || {}).value],
      [4, "sample-requested", "Water sample question", (s.sample || {}).requested],
    ];
    if ((s.sample || {}).requested === "Yes") {
      req.push([4, "sample-source", "Sample from", (s.sample || {}).source]);
      req.push([4, "sample-collected", "Sample collected?", (s.sample || {}).collected]);
      if ((s.sample || {}).collected === "No") req.push([4, "nc-reason", "Reason not collected", (s.sample || {}).not_collected_reason]);
    }
    return req.find(([, , , v]) => !v);
  };

  const complete = async () => {
    if (busy) return;
    const miss = missingRequired();
    if (miss) {
      const [mStep, tid, label] = miss;
      toast.error(`Please answer: ${label}`);
      setStep(mStep);
      setTimeout(() => {
        document.querySelector(`[data-testid="${tid}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 250);
      return;
    }
    setBusy(true);
    try {
      await save(true);
      const { data: r } = await api.post(`/field/inspection/${data.inspection.id}/complete`);
      setDone(r);
    } catch (e) { toast.error(e.response?.data?.detail || "Complete failed"); }
    finally { setBusy(false); }
  };

  if (loadError) {
    // Sep 2026 — this card used to be a dead end. "Enter a different PIN"
    // only did localStorage.removeItem() + reload, but technician PIN
    // sign-in ALSO sets the httpOnly `scwt_auth` cookie, and /auth/me
    // falls back to it — so the same technician was restored on reload
    // and the same request failed again, with no PIN screen ever shown.
    // It must go through logout() (which clears the cookie server-side)
    // and then land on /field/login carrying this job as `next`.
    const permanent = loadStatus === 403 || loadStatus === 404;
    const switchPin = async () => {
      if (switching) return;
      setSwitching(true);
      try {
        await logout();
      } catch {
        // Network died mid-signout — still send them to the PIN screen;
        // the stale cookie is rejected on the next request anyway.
      }
      navigate(`/field/login?next=${encodeURIComponent(`/field/inspection/${appointmentId}`)}`, { replace: true });
    };
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md w-full rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="field-inspection-error">
          <h2 className="text-lg font-bold text-slate-900">Can't open this inspection</h2>
          <p className="mt-2 text-sm text-slate-700">{loadError}</p>
          {user && (
            <p className="mt-3 text-xs text-slate-500" data-testid="field-inspection-signed-in-as">
              Signed in as <span className="font-semibold text-slate-700">{user.name || user.email}</span>
            </p>
          )}
          <div className="mt-4 flex flex-col gap-2">
            <button
              onClick={switchPin}
              disabled={switching}
              className={`rounded-full text-sm font-bold px-5 py-2.5 disabled:opacity-60 ${permanent ? "bg-slate-900 text-white" : "bg-white ring-1 ring-slate-300 text-slate-700"}`}
              data-testid="field-inspection-switch-pin"
            >
              {switching ? "Signing out…" : "Not you? Enter a different PIN"}
            </button>
            {!permanent && (
              <button onClick={load} className="rounded-full bg-slate-900 text-white text-sm font-bold px-5 py-2.5" data-testid="field-inspection-retry">Try again</button>
            )}
            {permanent && (
              <p className="text-[11px] text-slate-500">
                Retrying won't help — this job is assigned to someone else, or the
                link is out of date. Sign in with the assigned technician's PIN,
                or ask AWARE admin to reassign the job to you.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const a = data.appointment;
  const insp = data.inspection;
  const photos = data.photos || [];

  // In-Field Booking — instantly creates the 20% offer, stashes the prefill
  // blob for the booking page, and jumps to the cart (Tank 360 preselected).
  const startFieldBooking = async () => {
    if (bookBusy) return;
    setBookBusy(true);
    try {
      const { data: b } = await api.post(`/field/inspection/${insp.id}/book-test`);
      localStorage.setItem("aware_field_booking", JSON.stringify({
        appointment_id: appointmentId, inspection_id: insp.id,
        code: b.discount_code, customer: b.customer, ts: Date.now(),
      }));
      navigate("/book-water-test?field=1&test=tank&kit=pickup");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the booking");
      load();
    } finally { setBookBusy(false); }
  };

  const sendPaymentLink = async () => {
    if (linkBusy) return;
    setLinkBusy(true);
    try {
      const { data: r } = await api.post(`/field/inspection/${insp.id}/send-payment-link`);
      toast.success(`Payment link sent to ${r.sent_to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send payment link"); }
    finally { setLinkBusy(false); }
  };

  if (done) {
    const booked = data.booking?.booked;
    const pending = data.booking?.pending;
    const summary = data.booking?.summary;
    const techCollected = (pending?.collection_method || summary?.collection_method) === "technician_collected";
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10 max-w-md mx-auto text-center" data-testid="inspection-done">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
        <h1 className="mt-3 text-xl font-bold text-slate-900">Inspection completed</h1>
        <p className="mt-2 text-sm text-slate-600">{a.street_address}, {a.suburb} — your inspection has been submitted. AWARE is preparing the homeowner's Water Health Inspection Report.</p>
        {booked ? (
          <div data-testid="field-booking-confirmed" className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-300 p-4 text-left">
            <div className="text-sm font-bold text-emerald-800">✓ Payment received — lab test booked</div>
            <div className="mt-1 text-xs text-emerald-700">
              {data.booking.test_name ? `${data.booking.test_name} — ` : ""}Reference {data.booking.booking_reference}
            </div>
            {summary && (
              <div className="mt-3 pt-3 border-t border-emerald-200 text-xs text-slate-700 space-y-1.5" data-testid="field-booking-summary">
                <div className="flex justify-between"><span className="text-slate-500">Sample</span><b>{summary.sample_source || "—"}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Collection</span><b>{techCollected ? "✓ Collected by technician" : "Customer"}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Payment</span><b className="text-emerald-700">✓ Paid</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Customer email</span><b>{summary.confirmation_email === "technician_collected" ? "✓ Technician-collected template" : "✓ Standard confirmation"}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Collection instructions</span><b>{techCollected ? "Not required" : "Included"}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Return label</span><b>{techCollected ? "Not required" : "Standard"}</b></div>
              </div>
            )}
          </div>
        ) : pending ? (
          <div data-testid="field-payment-panel" className="mt-6 rounded-2xl bg-white border border-slate-200 p-4 text-left">
            <div className="text-sm font-bold text-slate-900">Payment required</div>
            <div className="mt-2.5 text-xs text-slate-700 space-y-1.5">
              <div className="flex justify-between"><span className="text-slate-500">Test</span><b>{pending.test_name}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">Sample</span><b>{pending.sample_source || "—"}</b></div>
              {techCollected && <div className="flex justify-between"><span className="text-slate-500">Collection</span><b>✓ Collected by technician</b></div>}
              <div className="flex justify-between"><span className="text-slate-500">Amount due</span><b>${Number(pending.amount || 0).toFixed(2)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">Payment</span><b className="text-amber-600">Pending</b></div>
            </div>
            {pending.payment_qr ? (
              <div className="mt-4 text-center">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Scan to pay securely</div>
                <img data-testid="field-payment-qr" src={pending.payment_qr} alt="Payment QR code"
                  className="mx-auto mt-2 w-44 h-44 rounded-xl border border-slate-200" />
                <div className="mt-2 text-[11px] text-slate-400 flex items-center justify-center gap-1.5" data-testid="field-payment-waiting">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> Waiting for payment…
                </div>
              </div>
            ) : (
              <p className="mt-3 text-xs text-amber-700" data-testid="field-payment-expired">Payment link expired — ask AWARE admin to send the customer a payment reminder.</p>
            )}
            <div className="mt-4 grid gap-2">
              <button data-testid="field-send-payment-link-btn" disabled={linkBusy || !pending.payment_url} onClick={sendPaymentLink}
                className="w-full py-3 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm disabled:opacity-50">
                {linkBusy ? "Sending…" : pending.payment_link_sent_at ? "RESEND PAYMENT LINK" : "SEND PAYMENT LINK"}
              </button>
              {pending.payment_url && (
                <a data-testid="field-open-payment-page" href={pending.payment_url} target="_blank" rel="noreferrer"
                  className="w-full py-3 rounded-full border border-slate-300 text-slate-700 font-bold text-sm text-center block">
                  OPEN PAYMENT PAGE
                </a>
              )}
            </div>
            {pending.payment_link_sent_at && (
              <p className="mt-2 text-[11px] text-emerald-700 font-semibold" data-testid="field-payment-link-sent">✓ Payment link emailed to the customer</p>
            )}
            <p className="mt-3 text-[11px] text-slate-400">You can leave this job with payment pending — the sample stays recorded as collected and laboratory testing is held until payment is confirmed.</p>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-4 text-left">
            <div className="text-sm font-bold text-slate-900">Owner wants a lab test?</div>
            <p className="mt-1 text-xs text-slate-500">Book it now on their behalf — details prefill automatically and their 20% inspection discount is applied instantly.</p>
            <button data-testid="book-lab-test-btn" disabled={bookBusy} onClick={startFieldBooking}
              className="mt-3 w-full py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm disabled:opacity-60">
              {bookBusy ? "Preparing booking…" : "Book Lab Test Now — 20% off"}
            </button>
          </div>
        )}
        <button data-testid="back-to-field-today" onClick={() => navigate("/field/today")}
          className="mt-5 text-xs font-bold text-cyan-700 underline underline-offset-2">← Back to today's Water Health Checks</button>
        <p className="mt-3 text-xs text-slate-400">You don't need to write anything — you can move to your next appointment.</p>
      </div>
    );
  }

  const tc = s.tank_config || {}, tw = s.tank_water || {}, tcon = s.tank_condition || {};
  const ca = s.catchment || {}, fl = s.filtration || {}, uv = s.uv || {}, us = s.under_sink || {}, smp = s.sample || {};
  const counts = tc.counts || {};
  const sedLvl = { "None visible": "optional", Light: "suggested", Moderate: "suggested", Heavy: "suggested", "Unable to assess": "hidden" }[tw.sediment_level] || "optional";
  const clarLvl = ["Slightly cloudy", "Cloudy", "Very cloudy"].includes(tw.clarity) ? "suggested" : "optional";
  const colLvl = tw.colour && !["Colourless", "Unable to assess"].includes(tw.colour) ? "suggested" : "optional";
  const visNotable = (tw.visible_condition || []).some((v) => v !== "No obvious issue");
  const gutLvl = null && "retired"; // eslint-disable-line no-unused-vars
  const catchLvl = ca.condition === "Needs Attention" ? "suggested"
    : ca.condition === "Unable to Confirm" ? "hidden" : "optional";
  const leadLvl = ["Yes", "Suspected"].includes(ca.lead_flashing) ? "suggested" : "optional";
  const filtLvl = (fl.condition || []).some((c) => ["Appears overdue", "Significant pressure restriction reported"].includes(c)) ? "suggested" : "optional";
  const uvLvl = (uv.powered === "No" || uv.service_history === "More than 12 months" || uv.observation === "Attention Suggested") ? "suggested" : "optional";
  const pb = (props) => <PhotoBtn insp={insp} photos={photos} onChange={reloadPhotos} {...props} />;

  const bySection = photos.reduce((acc, p) => { const k = p.section || "additional"; (acc[k] = acc[k] || []).push(p); return acc; }, {});

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 max-w-md mx-auto pb-28" data-testid="field-inspection">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-cyan-700">Water Health Inspection</div>
      <h1 data-testid="inspection-address" className="text-lg font-bold text-slate-900 mt-1">{a.street_address}, {a.suburb} {a.state} {a.postcode}</h1>
      <div className="text-sm text-slate-500">{a.first_name} · {a.mobile}</div>
      {(data.customer_concerns?.length > 0) && (
        <div data-testid="inspection-concerns" className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Customer concerns</div>
          {(() => { const shown = data.customer_concerns.filter((c) => !(c === "Other" && data.customer_concerns_other)); return shown.length > 0 && <div className="mt-1 text-sm font-semibold text-slate-900">{shown.join(" · ")}</div>; })()}
          {data.customer_concerns_other && <div className="mt-1 text-xs text-slate-600">"{data.customer_concerns_other}"</div>}
        </div>
      )}

      <div className="mt-4 flex gap-1" data-testid="step-bar">
        {STEPS.map((st, i) => (
          <button key={st} data-testid={`step-${i + 1}`} onClick={() => setStep(i)}
            className={`flex-1 py-1.5 rounded-full text-[10px] font-bold ${i === step ? "bg-cyan-600 text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1} {st}</button>
        ))}
      </div>

      {step === 0 && (
        <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-4" data-testid="step-tank-setup">
          <h2 className="font-bold text-slate-900 text-sm">1 · Tank Configuration</h2>
          <p className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="household-only-note">
            Only record tanks connected to, interconnected with, or capable of supplying the household water system. Do not include isolated garden-only tanks.</p>
          <Q label="Household tank quantities by material">
            {TANK_MATS.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between mt-2">
                <span className="text-[13px] text-slate-700">{label}</span>
                <div className="flex items-center gap-2">
                  <button type="button" data-testid={`tank-count-${k}-minus`} onClick={() => setTankCount(k, Math.max(0, (counts[k] || 0) - 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 font-bold text-slate-600">−</button>
                  <span data-testid={`tank-count-${k}`} className="w-6 text-center font-bold text-slate-900">{counts[k] || 0}</span>
                  <button type="button" data-testid={`tank-count-${k}-plus`} onClick={() => setTankCount(k, Math.min(20, (counts[k] || 0) + 1))} className="w-8 h-8 rounded-full border-2 border-cyan-500 font-bold text-cyan-700">+</button>
                </div>
              </div>
            ))}
          </Q>
          {(tc.tanks || []).map((t) => (
            <div key={t.key} data-testid={`tank-card-${t.key}`} className="mt-3 rounded-xl border border-cyan-200 bg-cyan-50/40 p-3">
              <div className="text-[13px] font-bold text-slate-800">{t.label}</div>
              <input data-testid={`tank-size-${t.key}`} maxLength={120} value={t.size_note || ""}
                onChange={(e) => setTank(t.key, { size_note: e.target.value })}
                placeholder='Approximate size (optional) — e.g. "23,000 L"'
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <input data-testid={`tank-note-${t.key}`} maxLength={300} value={t.note || ""}
                onChange={(e) => setTank(t.key, { note: e.target.value })}
                placeholder="Description / observations (optional)"
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              {pb({ category: "tank_photo", section: "tank_setup", questionKey: "tank_photo", tankKey: t.key, finding: t.label, label: "ADD TANK PHOTO" })}
            </div>
          ))}
          {tc.sizes_note ? ( /* legacy combined note — shown only when an old draft already has it */
            <Q label="Approximate sizes (legacy note)" hint="Recorded before per-tank details — kept for reference">
              <input data-testid="tank-sizes" maxLength={120} value={tc.sizes_note || ""} onChange={(e) => set("tank_config", { sizes_note: e.target.value })}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </Q>
          ) : null}
          <Q label="Are the household tanks interconnected?"><Pick tid="interconnected" options={OPTS.interconnected} value={tc.interconnected} onPick={(v) => set("tank_config", { interconnected: v })} /></Q>
          <Q label="Are the household tanks self-levelling?"><Pick tid="self-levelling" options={OPTS.self_levelling} value={tc.self_levelling} onPick={(v) => set("tank_config", { self_levelling: v })} /></Q>
          <Q label="Do these tanks supply the house?"><Pick tid="supplies-house" options={OPTS.supplies_house} value={tc.supplies_house} onPick={(v) => set("tank_config", { supplies_house: v })} /></Q>
        </section>
      )}

      {step === 1 && (
        <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-4" data-testid="step-water">
          <h2 className="font-bold text-slate-900 text-sm">2 · Tank Water Observations</h2>
          <Q label="Where was the tank water visually assessed or sampled?"><MultiPick tid="sample-point" options={OPTS.sample_points} values={tw.sample_points} onToggle={(o) => toggle("tank_water", "sample_points", o)} /></Q>
          <Q label="Was a sample/view taken from the bottom of the tank?"><Pick tid="bottom-sample" options={OPTS.bottom_sample} value={tw.bottom_sample} onPick={(v) => set("tank_water", { bottom_sample: v })} /></Q>
          <Q label="Sediment level"><Pick tid="sediment" options={OPTS.sediment} value={tw.sediment_level} onPick={(v) => set("tank_water", { sediment_level: v })} />
            {pb({ category: "bottom_sediment", section: "tank_water", questionKey: "sediment_level", finding: tw.sediment_level || "", level: sedLvl, label: "TAKE BOTTOM-OF-TANK / SEDIMENT PHOTO" })}</Q>
          <Q label="Water clarity"><Pick tid="clarity" options={OPTS.clarity} value={tw.clarity} onPick={(v) => set("tank_water", { clarity: v })} />
            {tw.clarity && tw.clarity !== "Unable to assess" && pb({ category: "water_clarity", section: "tank_water", questionKey: "clarity", finding: tw.clarity || "", level: clarLvl, label: "TAKE WATER SAMPLE PHOTO" })}</Q>
          <Q label="Water colour"><Pick tid="colour" options={OPTS.colour} value={tw.colour} onPick={(v) => set("tank_water", { colour: v })} />
            {tw.colour === "Other" && <input data-testid="colour-note" maxLength={120} placeholder="Optional short note" value={tw.colour_note || ""} onChange={(e) => set("tank_water", { colour_note: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />}
            {colLvl === "suggested" && pb({ category: "water_colour", section: "tank_water", questionKey: "colour", finding: tw.colour || "", level: "suggested", label: "TAKE WATER COLOUR PHOTO" })}</Q>
          <Q label="Odour"><Pick tid="odour" options={OPTS.odour} value={tw.odour} onPick={(v) => set("tank_water", { odour: v })} />
            {tw.odour === "Other" && <input data-testid="odour-note" maxLength={120} placeholder="Optional short note" value={tw.odour_note || ""} onChange={(e) => set("tank_water", { odour_note: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />}</Q>
          <Q label="Visible water condition"><MultiPick tid="visible" options={OPTS.visible} values={tw.visible_condition} onToggle={(o) => toggle("tank_water", "visible_condition", o)} />
            {visNotable && pb({ category: "water_condition", section: "tank_water", questionKey: "visible_condition", finding: (tw.visible_condition || []).filter((v) => v !== "No obvious issue").join("; "), level: "suggested" })}</Q>

          <h2 className="font-bold text-slate-900 text-sm mt-6">Tank Condition / Entry Points</h2>
          <Q label="Tank lid / access point"><Pick tid="lid" options={OPTS.lid} value={tcon.lid} onPick={(v) => set("tank_condition", { lid: v })} />
            {tcon.lid === "Attention Suggested" && pb({ category: "lid_access", section: "tank_condition", questionKey: "lid", finding: tcon.lid, level: "suggested" })}</Q>
          <Q label="Inlet screening"><Pick tid="inlet-screen" options={OPTS.screen_inlet} value={tcon.inlet_screen} onPick={(v) => set("tank_condition", { inlet_screen: v })} />
            {["Damaged", "Missing"].includes(tcon.inlet_screen) && pb({ category: "inlet_screen", section: "tank_condition", questionKey: "inlet_screen", finding: tcon.inlet_screen, level: "suggested" })}</Q>
          <Q label="Overflow screening"><Pick tid="overflow-screen" options={OPTS.screen} value={tcon.overflow_screen} onPick={(v) => set("tank_condition", { overflow_screen: v })} />
            {["Damaged", "Missing"].includes(tcon.overflow_screen) && pb({ category: "overflow_screen", section: "tank_condition", questionKey: "overflow_screen", finding: tcon.overflow_screen, level: "suggested" })}</Q>
          <Q label="Visible contamination entry points"><MultiPick tid="entry" options={OPTS.entry} values={tcon.entry_points} onToggle={(o) => toggle("tank_condition", "entry_points", o)} />
            {(tcon.entry_points || []).some((e) => e !== "None observed") && pb({ category: "entry_point", section: "tank_condition", questionKey: "entry_points", finding: (tcon.entry_points || []).filter((e) => e !== "None observed").join("; "), level: "suggested" })}</Q>
        </section>
      )}

      {step === 2 && (
        <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-4" data-testid="step-catchment">
          <h2 className="font-bold text-slate-900 text-sm">3 · Catchment</h2>
          <Q label="Overall condition of catchment area">
            <Pick tid="catch-condition" options={OPTS.catch_condition} value={ca.condition} onPick={(v) => set("catchment", { condition: v })} />
            {ca.condition && catchLvl !== "hidden" && pb({ category: "catchment", section: "catchment", questionKey: "condition", finding: ca.condition, level: catchLvl, label: "TAKE CATCHMENT PHOTO" })}</Q>
          <Q label="Lead flashing visible or suspected within the rainwater catchment?">
            <Pick tid="lead-flashing" options={OPTS.lead} value={ca.lead_flashing} onPick={(v) => set("catchment", { lead_flashing: v })} />
            {leadLvl === "suggested" && pb({ category: "lead_flashing", section: "catchment", questionKey: "lead_flashing", finding: ca.lead_flashing, level: "suggested", label: "TAKE LEAD FLASHING PHOTO" })}</Q>
        </section>
      )}

      {step === 3 && (
        <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-4" data-testid="step-treatment">
          <h2 className="font-bold text-slate-900 text-sm">4 · Whole-of-House Filtration</h2>
          <Q label="Whole-of-house filtration installed?"><Pick tid="filt-installed" options={YN_UC} value={fl.installed} onPick={(v) => set("filtration", { installed: v })} /></Q>
          {fl.installed === "Yes" && (<>
            <Q label="Configuration"><Pick tid="filt-config" options={OPTS.filt_config} value={fl.config} onPick={(v) => set("filtration", { config: v })} />
              {fl.config === "Other" && <input data-testid="filt-config-note" maxLength={120} placeholder="Optional description" value={fl.config_note || ""} onChange={(e) => set("filtration", { config_note: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />}</Q>
            <Q label="Filter type/media where visible or known"><MultiPick tid="filt-media" options={OPTS.filt_media} values={fl.media} onToggle={(o) => toggle("filtration", "media", o)} /></Q>
            <Q label="Filter condition / history"><MultiPick tid="filt-cond" options={OPTS.filt_cond} values={fl.condition} onToggle={(o) => toggle("filtration", "condition", o)} /></Q>
            {pb({ category: "filtration", section: "treatment", questionKey: "filtration", finding: (fl.condition || []).join("; "), level: filtLvl, label: "TAKE FILTRATION PHOTO" })}
          </>)}
          {fl.installed === "No" && pb({ category: "filtration_suggested_location", section: "treatment", questionKey: "filtration_suggested_location", label: "TAKE PHOTO OF SUGGESTED FILTRATION / PUMP LOCATION" })}
          {(fl.installed === "Yes" || fl.installed === "No") && (
            <Q label="Recommend System Upgrade"
              hint={fl.installed === "Yes"
                ? "Select where the existing system is inadequate, undersized, outdated, poorly configured or missing treatment stages — no written explanation needed"
                : "Recommends installation of a suitable whole-of-house filtration system"}>
              <Pick tid="filt-upgrade" options={["Selected", "Not selected"]}
                value={fl.recommend_upgrade === true ? "Selected" : fl.recommend_upgrade === false ? "Not selected" : null}
                onPick={(v) => set("filtration", { recommend_upgrade: v === "Selected" })} />
              {fl.recommend_upgrade === true && (
                <p className="mt-1.5 text-[11px] font-semibold text-cyan-700" data-testid="filt-upgrade-note">
                  Will appear on the report as “Suggested Upgrade — Whole-of-House Filtration”.
                </p>
              )}
            </Q>
          )}
          <h2 className="font-bold text-slate-900 text-sm mt-6">UV</h2>
          <Q label="UV installed?"><Pick tid="uv-installed" options={YN_UC} value={uv.installed} onPick={(v) => set("uv", { installed: v })} /></Q>
          {uv.installed === "Yes" && (<>
            <Q label="UV powered at inspection?"><Pick tid="uv-powered" options={YN_UC} value={uv.powered} onPick={(v) => set("uv", { powered: v })} /></Q>
            <Q label="UV service history"><Pick tid="uv-service" options={OPTS.uv_service} value={uv.service_history} onPick={(v) => set("uv", { service_history: v })} /></Q>
            <Q label="UV observation"><Pick tid="uv-obs" options={OPTS.uv_obs} value={uv.observation} onPick={(v) => set("uv", { observation: v })} /></Q>
            {pb({ category: "uv", section: "treatment", questionKey: "uv", finding: uv.observation || "", level: uvLvl, label: "TAKE UV PHOTO" })}
          </>)}
        </section>
      )}

      {step === 4 && (
        <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-4" data-testid="step-sample">
          <h2 className="font-bold text-slate-900 text-sm">5 · Water Testing History</h2>
          <Q label="When was the household water last laboratory tested?">
            <Pick tid="hist" options={OPTS.history} value={(s.testing_history || {}).value} onPick={(v) => set("testing_history", { value: v })} /></Q>

          <h2 className="font-bold text-slate-900 text-sm mt-6">Water Sample</h2>
          <Q label="Would the owner like a water sample taken for laboratory testing today?">
            <Pick tid="sample-requested" options={["Yes", "No"]} value={smp.requested} onPick={(v) => set("sample", { requested: v })} /></Q>
          {smp.requested === "Yes" && (<>
            <Q label="Sample from"><Pick tid="sample-source" options={OPTS.sample_src} value={smp.source} onPick={(v) => set("sample", { source: v })} /></Q>
            <Q label="Sample collected?"><Pick tid="sample-collected" options={["Yes", "No"]} value={smp.collected} onPick={(v) => set("sample", { collected: v })} /></Q>
            {smp.collected === "Yes" && pb({ category: "lab_sample", section: "sample", questionKey: "lab_sample", finding: smp.source || "", label: "TAKE SAMPLE PHOTO" })}
            {smp.collected === "No" && (
              <Q label="Reason not collected"><Pick tid="nc-reason" options={OPTS.nc_reason} value={smp.not_collected_reason} onPick={(v) => set("sample", { not_collected_reason: v })} /></Q>
            )}
          </>)}

          <h2 className="font-bold text-slate-900 text-sm mt-6">Additional Photos</h2>
          <p className="text-[11px] text-slate-500">Only for anything unusual that didn't fit the questions above.</p>
          {pb({ category: "additional", section: "additional", label: "ADD PHOTO" })}

          <h2 className="font-bold text-slate-900 text-sm mt-6">Additional Observation <span className="font-normal text-slate-400">(optional)</span></h2>
          <textarea data-testid="tech-note" placeholder="Optional — most inspections won't need this" maxLength={500}
            value={(s.tech_note || {}).text || ""} onChange={(e) => set("tech_note", { text: e.target.value })}
            className="mt-2 w-full rounded-xl border border-slate-200 p-2 text-sm" rows={3} />
          <div className="text-right text-[10px] text-slate-400">{((s.tech_note || {}).text || "").length}/500</div>

          <h2 className="font-bold text-slate-900 text-sm mt-6">Photo Review ({photos.length})</h2>
          <div data-testid="photo-review" className="mt-2 space-y-3">
            {Object.entries(bySection).map(([sec, list]) => (
              <div key={sec}>
                <div className="text-[11px] font-bold text-slate-500">{SECTION_LABELS[sec] || sec} · {list.length} photo{list.length > 1 ? "s" : ""}</div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {list.map((p) => <Thumb key={p.id} inspId={insp.id} photo={p}
                    onRemove={async () => { try { await api.delete(`/field/inspection/${insp.id}/photos/${p.id}`); reloadPhotos(); } catch { toast.error("Remove failed"); } }} />)}
                </div>
              </div>
            ))}
            {photos.length === 0 && <div className="text-xs text-slate-400">No photos yet</div>}
          </div>

          <button data-testid="complete-inspection" disabled={busy} onClick={complete}
            className="mt-6 w-full py-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base disabled:opacity-60">
            {busy ? "Submitting…" : "Complete Inspection"}
          </button>
        </section>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-white/95 border-t border-slate-200 backdrop-blur px-4 py-3">
        <div className="max-w-md mx-auto flex gap-2">
          <button data-testid="step-prev" disabled={step === 0} onClick={() => { save(true); setStep(step - 1); }}
            className="flex-1 py-3 rounded-full border-2 border-slate-300 text-slate-600 font-bold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-1"><ChevronLeft className="w-4 h-4" /> Back</button>
          {step < 4 ? (
            <button data-testid="step-next" onClick={() => { save(true); setStep(step + 1); }}
              className="flex-1 py-3 rounded-full bg-cyan-600 text-white font-bold text-sm inline-flex items-center justify-center gap-1">Next <ChevronRight className="w-4 h-4" /></button>
          ) : (
            <button data-testid="save-draft" onClick={() => save(false)} className="flex-1 py-3 rounded-full border-2 border-cyan-500 text-cyan-700 font-bold text-sm">Save draft</button>
          )}
        </div>
      </div>
    </div>
  );
}
